import styled from 'styled-components';
import RadioBtn from '../../elements/RadioBtn';
import {
  selectApplication,
  selectAreAnyBlanketAIItemsChecked,
  selectHasBlanketAIBlanketAdditionalInsured,
  selectHasBlanketAIConstructionContractRequirementEndorsementChecked,
  selectHasBlanketAILessorOfLeasedEquipmentEndorsementChecked,
  selectHasBlanketAIManagersOrLessorsPremisesEndorsementChecked,
  selectHasBlanketAIPrimaryAndNonContributoryChecked,
  selectHasBlanketAIWaiverofSubrogation,
  selectHasBlanketAIWaiverOfTransferChecked,
} from '../../selectors/application';
import {
  removeBlanketAdditionalInsureds,
  setBlanketAdditionalInsureds,
  toggleBlanketAIItem,
} from '../../actions/application';
import { connect } from 'react-redux';
import Checkbox, { CheckboxAndLabel, CheckboxLabel } from '../../elements/Checkbox';
import SmoothCollapse from 'react-smooth-collapse';
import { useState } from 'react';
import { BlanketAIItem, PolicyType } from '../../types/enums';
import { RadioContainer, RadioText } from '../../Pages/AdditionalInsureds';
import { useFeatureFlags } from '../../toggle_tools/featureFlagTools';
import BOPIcon from '../../icons/BOPicon.svg';
import GLIcon from '../../icons/GLicon.svg';
import PLIcon from '../../icons/PLicon.svg';

const managerOrLessorDefaultFormText =
  'Any person or organization with whom you agree in a written agreement for lease or rental of premises or building, prior to loss, to name as an additional insured in this Businessowners Coverage.';
const lessorOfLeasedDefaultFormText =
  'Any person or organization with whom you agree in a written agreement for lease or rental of equipment, prior to loss, to name as an additional insured in this Businessowners Coverage.';
const blanketAdditionalInsuredsDefaultFormText =
  'Blanket Additional Insured automatically provides coverage to any party to which the policyholder is contractually required to provide coverage.  A one-time premium charge applies.';
const waiverOfSubrogationDefaultFormText =
  'The waiver of subrogation waives the right of Coterie to seek redress for losses from a negligent third party. A one-time premium charge applies.';

export const blanketOptions = [
  {
    question: 'Managers or Lessors Premises Additional Insured',
    label: 'additionalInsuredsManagersOrLessorsPremisesEndorsement',
    description: managerOrLessorDefaultFormText,
    id: BlanketAIItem.managersOrLessorsPremises,
    policyTypes: [PolicyType.BOP, PolicyType.GL],
  },
  {
    question: 'Lessor of Leased Equipment Additional Insured',
    label: 'additionalInsuredsLessorOfLeasedEquipmentEndorsement',
    description: lessorOfLeasedDefaultFormText,
    id: BlanketAIItem.lessorOfLeasedEquipment,
    policyTypes: [PolicyType.BOP, PolicyType.GL],
  },
  {
    question: 'Construction Contract Requirement Additional Insured',
    label: 'additionalInsuredsConstructionContractRequirementEndorsement',
    id: BlanketAIItem.constructionContractRequirement,
    policyTypes: [PolicyType.BOP, PolicyType.GL],
  },
  {
    question: 'Primary and Non-Contributory',
    label: 'additionalInsuredsPrimaryAndNonContributory',
    id: BlanketAIItem.primaryAndNonNonContributory,
    policyTypes: [PolicyType.BOP, PolicyType.GL],
  },
  {
    question: 'Waiver of Subrogation',
    label: 'additionalInsuredsWaiverOfTransfer',
    id: BlanketAIItem.waiverOfTransfer,
    policyTypes: [PolicyType.BOP, PolicyType.GL],
  },
  {
    question: 'Waiver of Subrogation',
    label: 'additionalInsuredsWaiverOfSubrogation',
    description: waiverOfSubrogationDefaultFormText,
    id: BlanketAIItem.waiverOfSubrogation,
    policyTypes: [PolicyType.PL],
  },
  {
    question: 'Blanket Additional Insured',
    label: 'additionalInsuredsBlanketAdditionalInsureds',
    description: blanketAdditionalInsuredsDefaultFormText,
    id: BlanketAIItem.blanketAdditionalInsureds,
    policyTypes: [PolicyType.PL],
  },
];

interface BlanketAdditionalInsuredsProps {
  toggleBlanketAIItem: (arg0: BlanketAIItem) => void;
  removeBlanketAdditionalInsureds: () => void;
  setBlanketAdditionalInsureds: () => void;
  hasBlanketAIPrimaryAndNonContributoryChecked: boolean | undefined;
  hasBlanketAIWaiverOfTransferChecked: boolean | undefined;
  hasBlanketAIConstructionContractRequirementEndorsementChecked: boolean | undefined;
  hasBlanketAIManagersOrLessorsPremisesEndorsementChecked: boolean | undefined;
  hasBlanketAILessorOfLeasedEquipmentEndorsementChecked: boolean | undefined;
  hasBlanketAIWaiverOfSubrogationChecked: boolean | undefined;
  hasBlanketAIAdditionalInsuredsChecked: boolean | undefined;
  areAnyBlanketAIItemsChecked: boolean;
  applicationTypes?: PolicyType[];
}

const BlanketAdditionalInsureds = ({
  toggleBlanketAIItem,
  hasBlanketAIPrimaryAndNonContributoryChecked,
  hasBlanketAIWaiverOfTransferChecked,
  hasBlanketAIConstructionContractRequirementEndorsementChecked,
  hasBlanketAIManagersOrLessorsPremisesEndorsementChecked,
  hasBlanketAILessorOfLeasedEquipmentEndorsementChecked,
  hasBlanketAIAdditionalInsuredsChecked,
  hasBlanketAIWaiverOfSubrogationChecked,
  removeBlanketAdditionalInsureds,
  setBlanketAdditionalInsureds,
  areAnyBlanketAIItemsChecked,
  applicationTypes,
}: BlanketAdditionalInsuredsProps) => {
  const { showPlv2 } = useFeatureFlags();
  const [blanketAISelected, setBlanketAISelected] = useState(areAnyBlanketAIItemsChecked);
  const showAdditionalInsuredsForm = () => {
    setBlanketAISelected(true);
    setBlanketAdditionalInsureds();
  };

  const hideAdditionalInsuredsForm = () => {
    setBlanketAISelected(false);
    removeBlanketAdditionalInsureds();
  };

  const blanketAIItemsSelected = {
    [BlanketAIItem.primaryAndNonNonContributory]: hasBlanketAIPrimaryAndNonContributoryChecked,
    [BlanketAIItem.waiverOfTransfer]: hasBlanketAIWaiverOfTransferChecked,
    [BlanketAIItem.constructionContractRequirement]: hasBlanketAIConstructionContractRequirementEndorsementChecked,
    [BlanketAIItem.managersOrLessorsPremises]: hasBlanketAIManagersOrLessorsPremisesEndorsementChecked,
    [BlanketAIItem.lessorOfLeasedEquipment]: hasBlanketAILessorOfLeasedEquipmentEndorsementChecked,
    [BlanketAIItem.waiverOfSubrogation]: hasBlanketAIWaiverOfSubrogationChecked,
    [BlanketAIItem.blanketAdditionalInsureds]: hasBlanketAIAdditionalInsuredsChecked,
  };

  return (
    <>
      <RadioContainer data-cy="blanked-additional-insureds">
        <RadioText>Include blanket additional insured packages on this policy?</RadioText>

        <RadioBtn
          name="blanket-additional-insured"
          id="yes-blanket"
          labelText="Yes"
          isChecked={blanketAISelected}
          onChange={showAdditionalInsuredsForm}
        />
        <RadioBtn
          name="blanket-additional-insured"
          id="no-blanket"
          labelText="No"
          isChecked={!blanketAISelected}
          onChange={hideAdditionalInsuredsForm}
        />
      </RadioContainer>
      <SmoothCollapse expanded={blanketAISelected} allowOverflowWhenOpen={true}>
        <CheckboxContainer>
          <BlanketLegend>
            These bundles include commonly-requested forms with blanket language to comply with written contractual
            agreements.
          </BlanketLegend>
          {!showPlv2 && (
            <div>
              {blanketOptions
                .filter(
                  (option) =>
                    option.id !== BlanketAIItem.waiverOfSubrogation &&
                    option.id !== BlanketAIItem.blanketAdditionalInsureds
                )
                .map((option) => {
                  return (
                    <span key={option.id}>
                      <Checkbox
                        onChange={() => toggleBlanketAIItem(option.id)}
                        selected={Boolean(blanketAIItemsSelected[option.id])}
                        label={option.question}
                        id={option.id}
                      />
                      {option?.description && (
                        <BlanketDescription>
                          Blanket wording: <br /> {option.description}
                        </BlanketDescription>
                      )}
                    </span>
                  );
                })}
            </div>
          )}
          {showPlv2 && (
            <div>
              {applicationTypes?.sort()?.map((type) => {
                const option = {
                  [PolicyType.BOP]: { icon: BOPIcon, title: "Additional Insureds - Business Owner's Policy" },
                  [PolicyType.GL]: { icon: GLIcon, title: 'Additional Insureds - General Liability' },
                  [PolicyType.PL]: { icon: PLIcon, title: 'Additional Insureds - Professional Liability' },
                };
                const options = blanketOptions.filter((option) => option.policyTypes.includes(type));
                return (
                  <BlanketAdditionalInsuredsCard key={type}>
                    <BlanketAdditionalInsuredsCardHeader>
                      <div>
                        <img src={option[type].icon} alt={`${type} icon`} />
                      </div>
                      <h3>{option[type].title}</h3>
                    </BlanketAdditionalInsuredsCardHeader>
                    <BlanketAdditionalInsuredsCardContent>
                      {options.map((option) => (
                        <span key={option.id}>
                          <Checkbox
                            onChange={() => toggleBlanketAIItem(option.id)}
                            selected={Boolean(blanketAIItemsSelected[option.id])}
                            label={option.question}
                            id={option.id}
                          />
                          {option?.description && (
                            <BlanketDescription>
                              Blanket wording: <br /> {option.description}
                            </BlanketDescription>
                          )}
                        </span>
                      ))}
                    </BlanketAdditionalInsuredsCardContent>
                  </BlanketAdditionalInsuredsCard>
                );
              })}
            </div>
          )}
        </CheckboxContainer>
      </SmoothCollapse>
    </>
  );
};

const BlanketLegend = styled.legend`
  font-family: ${(props) => props.theme.font.typeface.secondary};
`;

const CheckboxContainer = styled.fieldset`
  border: none;
  margin: 0;
  margin-top: 8px;
  padding: 0;
  margin-bottom: 32px;

  legend {
    ${({ theme }) => theme.colors.neutral.dark}
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
  }

  ${CheckboxLabel} {
    ${({ theme }) => theme.colors.neutral.dark}
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
  }

  ${CheckboxAndLabel} {
    margin-top: 16px;
    margin-bottom: 0px;
  }
`;

const BlanketDescription = styled.p`
  line-height: 20px;
  color: #4a4a4d;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  font-family: ${(props) => props.theme.font.typeface.secondary};
  margin-top: 0;
  margin-left: 35px;
`;

const BlanketAdditionalInsuredsCard = styled.div`
  background-color: ${(props) => props.theme.colors.neutral.special};
  border-radius: 4px 4px 0px 0px;
  margin: 24px 0;
`;

const BlanketAdditionalInsuredsCardHeader = styled.header`
  display: flex;
  align-items: center;
  padding: 16px 16px 0 16px;
  gap: 16px;

  h3 {
    color: #0d0d46;
    font-family: ${(props) => props.theme.font.typeface.secondary};
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
  }
`;

const BlanketAdditionalInsuredsCardContent = styled.section`
  padding: 8px 32px 32px 32px;
`;

const mapStateToProps = (state: ReduxState) => ({
  hasBlanketAIPrimaryAndNonContributoryChecked: selectHasBlanketAIPrimaryAndNonContributoryChecked(state),
  hasBlanketAIWaiverOfTransferChecked: selectHasBlanketAIWaiverOfTransferChecked(state),
  hasBlanketAIConstructionContractRequirementEndorsementChecked:
    selectHasBlanketAIConstructionContractRequirementEndorsementChecked(state),
  hasBlanketAIManagersOrLessorsPremisesEndorsementChecked:
    selectHasBlanketAIManagersOrLessorsPremisesEndorsementChecked(state),
  hasBlanketAILessorOfLeasedEquipmentEndorsementChecked:
    selectHasBlanketAILessorOfLeasedEquipmentEndorsementChecked(state),
  hasBlanketAIWaiverOfSubrogationChecked: selectHasBlanketAIWaiverofSubrogation(state),
  hasBlanketAIAdditionalInsuredsChecked: selectHasBlanketAIBlanketAdditionalInsured(state),
  areAnyBlanketAIItemsChecked: selectAreAnyBlanketAIItemsChecked(state),
  applicationTypes: selectApplication(state).applicationTypes,
});

const mapDispatchToProps = {
  toggleBlanketAIItem,
  removeBlanketAdditionalInsureds,
  setBlanketAdditionalInsureds,
};

export default connect(mapStateToProps, mapDispatchToProps)(BlanketAdditionalInsureds);
